/**
 * Centralized configuration for API URLs and common utility functions
 * This file centralizes all URL construction logic to avoid repetitive code
 */

/**
 * Get the backend base URL (without /v1)
 * Defaults to http://localhost:3041
 */
export const getBackendBase = (): string => {
    return (
        (import.meta as any).env?.VITE_BACKEND_URL ||
        (import.meta as any).env?.VITE_API_URL ||
        'http://localhost:3041'
    );
};

/**
 * Get the full API URL with /v1 suffix
 * Example: http://localhost:3041/v1
 */
export const getApiUrl = (): string => {
    return `${getBackendBase()}/v1`;
};

/**
 * Construct image URL for group tours from database imageUrl
 * Handles various formats of imageUrl from API
 * @param imageUrl - URL from database (can be filename, /uploads/..., or full URL)
 * @returns Full URL to image or placeholder
 */
export const getGroupTourImageUrl = (imageUrl: string | null | undefined): string => {
    if (!imageUrl) {
        return 'https://via.placeholder.com/640x360?text=No+image';
    }

    // If already a full URL, return as-is
    if (imageUrl.startsWith('http')) {
        return imageUrl;
    }

    const backendBase = getBackendBase();

    // If starts with /uploads, just prepend backend base
    if (imageUrl.startsWith('/uploads')) {
        return `${backendBase}${imageUrl}`;
    }

    // Otherwise treat as filename and construct proper path
    const filename = imageUrl.split('/').pop();
    return `${backendBase}/uploads/group-tours/${filename}`;
};

/**
 * Construct image URL for experiences from database imageUrl
 * Handles various formats of imageUrl from API
 * @param imageUrl - URL from database (can be filename, /uploads/..., or full URL)
 * @returns Full URL to image or placeholder
 */
export const getExperienceImageUrl = (imageUrl: string | null | undefined): string => {
    if (!imageUrl) {
        return 'https://via.placeholder.com/640x360?text=No+image';
    }

    // If already a full URL, return as-is
    if (imageUrl.startsWith('http')) {
        return imageUrl;
    }

    const backendBase = getBackendBase();

    // If starts with /uploads, just prepend backend base
    if (imageUrl.startsWith('/uploads')) {
        return `${backendBase}${imageUrl}`;
    }

    // Otherwise treat as filename and construct proper path
    const filename = imageUrl.split('/').pop();
    return `${backendBase}/uploads/experience/${filename}`;
};

/**
 * Get base URL for constructing image paths (without /v1)
 * Used internally for image URL construction
 * @returns Backend base URL without API version
 */
export const getImageBase = (): string => {
    return getBackendBase();
};
